import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NewNavImproved from "../../components/Nav/NewNavImproved.tsx";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import Breadcrumbs from "../../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import PeopleSearchLayout from "../../components/PeopleSearch/PeopleSearchLayout.tsx";
import PeopleSearchHero from "../../components/PeopleSearch/PeopleSearchHero.tsx";
import layoutStyles from "../../components/PeopleSearch/PeopleSearchLayout.module.scss";
import PeopleSearchFilters from "../../components/PeopleSearch/PeopleSearchFilters.tsx";
import PeopleSearchResults from "../../components/PeopleSearch/PeopleSearchResults.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import useProfesionalesBootstrap from "../../hooks/useProfesionalesBootstrap.ts";
import useProfesionalesFilters from "../../hooks/useProfesionalesFilters.ts";
import usePremiumCheck from "../../hooks/usePremiumCheck";
import {
  selectFilteredMeta,
  selectFilteredUsers,
  selectJurisdictions,
  selectRegisteredUsers,
  selectSkillsList,
} from "../../store/selectors/profesionales.ts";
import { fetchFilteredProfessionals } from "../../store/profesionales/thunks.ts";
import { sanitizeProfessionals, profesionalesService } from "../../services/profesionalesService.ts";
import {
  PeopleSearchProfile,
  PeopleSearchTypeOption,
  PeopleSearchProfileType,
} from "../../types/peopleSearch.ts";
import { selectPeopleSearchWorkers } from "../../store/selectors/peopleSearch.ts";
import { fetchWorkersByFilters, fetchAllWorkers } from "../../store/peopleSearch/thunks.ts";
import { setPeopleSearchWorkers } from "../../store/peopleSearch/actions.ts";
import { openSendContactNotificationModal } from "../../store/actions";

type SkillOption = {
  id: number;
  name: string;
};

const DEFAULT_PAGE_SIZE = 12;

const buildDisplayName = (professional: any) => {
  if (professional.type === "COMPANY") {
    return professional.razon_social ?? professional.name ?? "";
  }
  const name = professional.name ?? "";
  const lastName = professional.last_name ?? "";
  return `${name} ${lastName}`.trim() || "Profesional";
};

const mapProfessionalToProfile = (
  professional: any,
  source: "FEATURED" | "FILTERED" = "FILTERED"
): PeopleSearchProfile => {
  const rawType = professional.type === "COMPANY" ? "COMPANY" : "PERSON";
  const type: PeopleSearchProfileType = source === "FEATURED"
    ? "FEATURED"
    : rawType === "COMPANY"
      ? "PROFESSIONAL_COMPANY"
      : "PROFESSIONAL_PERSON";

  const skillsSource = professional.skill ?? professional.skills ?? [];
  const skills = skillsSource
    .map((item: any) => {
      const entity = item?.skill ?? item;
      if (!entity?.id) return null;
      return {
        id: Number(entity.id),
        name: String(entity.name ?? ""),
      };
    })
    .filter((skill: any) => skill && !Number.isNaN(skill.id));

  const jurisdictions: Array<{ id: number; name: string }> = [];
  if (professional.zone) {
    jurisdictions.push({ id: 0, name: professional.zone });
  }

  return {
    id: String(professional.id),
    type,
    displayName: buildDisplayName(professional),
    companyName: professional.razon_social,
    avatarUrl: professional.image_profile ?? null,
    skills,
    jurisdictions,
    contact: {
      phone: professional.phone,
      email: professional.username,
      website: professional.url_site_web,
    },
    tags: [],
    rating: professional.stars ?? null,
    portfolioCount: professional.portfolio?.length ?? 0,
    availability: null,
    unemploymentFund: undefined,
    hasIdentityValidated: Boolean(professional.validated ?? true),
    rawType,
    source,
    raw: professional,
  };
};

const PeopleSearchPage = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const premiumCheck = usePremiumCheck();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
const { isLoading, jurisdictionsLoaded } = useProfesionalesBootstrap();

  const registeredUsers = useSelector(selectRegisteredUsers) as any[];
  const skillsList = useSelector(selectSkillsList) as any[];
  const jurisdictionsRaw = useSelector(selectJurisdictions) as any[];
  const filteredUsers = useSelector(selectFilteredUsers) as any[];
  const filteredMeta = useSelector(selectFilteredMeta) as any;
  const workerResults = useSelector(selectPeopleSearchWorkers);

  const sanitizedRegisteredUsers = useMemo(
    () => sanitizeProfessionals(registeredUsers),
    [registeredUsers]
  );

  const [typeFilterState, _setTypeFilter] = useState<PeopleSearchTypeOption>("FEATURED");
  const typeFilterRef = useRef<PeopleSearchTypeOption>("FEATURED");
  const setTypeFilter = useCallback((nextType: PeopleSearchTypeOption) => {
    typeFilterRef.current = nextType;
    _setTypeFilter(nextType);
  }, []);
  const exitFeaturedMode = useCallback(() => {
    if (typeFilterRef.current === "FEATURED") {
      typeFilterRef.current = "ALL";
      _setTypeFilter("ALL");
    }
  }, [_setTypeFilter]);
  const typeFilter = typeFilterState;
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [heroCollapsed, setHeroCollapsed] = useState(false);
  const hasFetchedAllWorkersRef = useRef(false);
  const allWorkersBaselineRef = useRef<PeopleSearchProfile[]>([]);

  const requestAllWorkers = useCallback(() => {
    if (hasFetchedAllWorkersRef.current) {
      return;
    }
    hasFetchedAllWorkersRef.current = true;
    dispatch(fetchAllWorkers());
  }, [dispatch]);

  const clearWorkers = useCallback(() => {
    if (!hasFetchedAllWorkersRef.current && workerResults.length === 0) {
      return;
    }
    hasFetchedAllWorkersRef.current = false;
    dispatch(setPeopleSearchWorkers([]));
  }, [dispatch, workerResults.length]);

  const handleFilterApply = useCallback(
    ({ skillIds, jurisdictionIds, allCountry, page }) => {
      const hasFilters =
        (skillIds?.length ?? 0) > 0 ||
        (jurisdictionIds?.length ?? 0) > 0 ||
        Boolean(allCountry) ||
        verifiedOnly;
      const currentType = typeFilterRef.current;
      if (currentType === "FEATURED" && !hasFilters) {
        clearWorkers();
        return;
      }
      if (hasFilters) {
        exitFeaturedMode();
      }
      const typeParam = currentType === "PROFESSIONAL"
        ? "PERSON"
        : currentType === "COMPANY"
          ? "COMPANY"
          : undefined;

      const shouldFetchProfessionals = currentType !== "WORKER";
      const shouldFetchWorkers = currentType === "WORKER" || currentType === "ALL";

      if (shouldFetchProfessionals) {
        dispatch(
          fetchFilteredProfessionals({
            skillIds,
            jurisdictionIds,
            allCountry,
            page,
            pageSize: DEFAULT_PAGE_SIZE,
            type: typeParam,
          })
        );
      }

      if (shouldFetchWorkers) {
        if (hasFilters) {
          dispatch(
            fetchWorkersByFilters({
              jurisdictions: allCountry ? undefined : jurisdictionIds,
              skills: skillIds,
              identityValidated: verifiedOnly ? true : undefined,
            })
          );
        } else {
          requestAllWorkers();
        }
      } else {
        clearWorkers();
      }
    },
    [dispatch, exitFeaturedMode, verifiedOnly, requestAllWorkers, clearWorkers]
  );


  const {
    state: filterState,
    actions: filterActions,
    meta: filtersMeta,
  } = useProfesionalesFilters({
    onApply: handleFilterApply,
    initialPage: filteredMeta?.page || 1,
  });

  const featuredProfiles = useMemo(
    () => (sanitizedRegisteredUsers ?? []).filter(Boolean).map((professional) => mapProfessionalToProfile(professional, "FEATURED")),
    [sanitizedRegisteredUsers]
  );

  const filteredProfessionalProfiles = useMemo(
    () => (filteredUsers ?? []).filter(Boolean).map((professional) => mapProfessionalToProfile(professional, "FILTERED")),
    [filteredUsers]
  );

  const [skillCatalog, setSkillCatalog] = useState<SkillOption[]>([]);

  const hasFilterSelections =
    filterState.skillIds.length > 0 ||
    filterState.jurisdictionIds.length > 0 ||
    filterState.allCountry;

  useEffect(() => {
    if (!hasFilterSelections && !verifiedOnly && workerResults.length > 0) {
      allWorkersBaselineRef.current = workerResults;
    }
  }, [workerResults, hasFilterSelections, verifiedOnly]);

  const showFeatured = typeFilter === "FEATURED" && !hasFilterSelections && !verifiedOnly;
  const noFilterConstraints = !hasFilterSelections && !verifiedOnly;

  useEffect(() => {
    if ((hasFilterSelections || verifiedOnly) && typeFilterRef.current === "FEATURED") {
      exitFeaturedMode();
    }
  }, [hasFilterSelections, verifiedOnly, exitFeaturedMode]);

  useEffect(() => {
    let isActive = true;
    const loadCatalog = async () => {
      try {
        const catalog = await profesionalesService.fetchSkillCatalog();
        const normalized = catalog
          .map((item) => ({
            id: item.id,
            name: item.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (isActive) {
          setSkillCatalog(normalized);
        }
      } catch (error) {
        console.error("fetchSkillCatalog", error);
      }
    };
    loadCatalog();
    return () => {
      isActive = false;
    };
  }, []);

  const buildSkillStats = useCallback(
    (source: PeopleSearchProfile[]) => {
      const stats = new Map<number, { name: string; count: number }>();
      source.forEach((profile) => {
        const unique = new Set<number>();
        profile.skills.forEach((skill) => {
          if (!unique.has(skill.id)) {
            unique.add(skill.id);
            const current = stats.get(skill.id);
            if (current) {
              stats.set(skill.id, {
                name: current.name,
                count: current.count + 1,
              });
            } else {
              stats.set(skill.id, {
                name: skill.name,
                count: 1,
              });
            }
          }
        });
      });
      return Array.from(stats.entries())
        .map(([id, value]) => ({ id, name: value.name, count: value.count }))
        .sort((a, b) => {
          const diff = b.count - a.count;
          if (diff !== 0) return diff;
          return a.name.localeCompare(b.name);
        });
    },
    []
  );

  const buildJurisdictionStats = useCallback(
    (source: PeopleSearchProfile[]) => {
      const stats = new Map<number, { name: string; count: number }>();
      source.forEach((profile) => {
        const unique = new Set<number>();
        profile.jurisdictions.forEach((jur) => {
          if (!unique.has(jur.id)) {
            unique.add(jur.id);
            const current = stats.get(jur.id);
            if (current) {
              stats.set(jur.id, {
                name: current.name,
                count: current.count + 1,
              });
            } else {
              stats.set(jur.id, {
                name: jur.name,
                count: 1,
              });
            }
          }
        });
      });
      return Array.from(stats.entries())
        .map(([id, value]) => ({ id, name: value.name, count: value.count }))
        .sort((a, b) => {
          const diff = b.count - a.count;
          if (diff !== 0) return diff;
          return a.name.localeCompare(b.name);
        });
    },
    []
  );

  const workerDataSource =
    (hasFilterSelections || verifiedOnly) && allWorkersBaselineRef.current.length > 0
      ? allWorkersBaselineRef.current
      : workerResults;

  const workerSkillStats = useMemo(
    () => buildSkillStats(workerDataSource),
    [buildSkillStats, workerDataSource]
  );

  const workerSkillOptions = useMemo(
    () =>
      workerSkillStats.map((skill) => ({
        id: skill.id,
        name: skill.name,
      })),
    [workerSkillStats]
  );

  const popularSkills = useMemo(
    () =>
      skillsList
        .map((skill: any) => ({
          id: Number(skill.id),
          name: typeof skill.name === "string" ? skill.name : String(skill.name ?? ""),
          users: typeof skill.users === "number" ? skill.users : 0,
        }))
        .sort((a, b) => {
          const diff = b.users - a.users;
          if (diff !== 0) return diff;
          return a.name.localeCompare(b.name);
        })
        .map(({ id, name }) => ({ id, name })),
    [skillsList]
  );

  const quickSkills = useMemo(() => {
    if (typeFilter === "WORKER") {
      const workerTop = workerSkillOptions.slice(0, 4);
      return workerTop.length > 0 ? workerTop : popularSkills.slice(0, 4);
    }
    return popularSkills.slice(0, 4);
  }, [typeFilter, workerSkillOptions, popularSkills]);

  const baseSkillOptions = useMemo(
    () => (skillCatalog.length > 0 ? skillCatalog : popularSkills),
    [skillCatalog, popularSkills]
  );

  const skillOptions = useMemo(() => {
    if (typeFilter === "WORKER") {
      return workerSkillOptions;
    }
    if (typeFilter === "COMPANY") {
      return baseSkillOptions;
    }
    if (typeFilter === "PROFESSIONAL") {
      return baseSkillOptions;
    }
    if (typeFilter === "ALL") {
      const map = new Map<number, string>();
      baseSkillOptions.forEach((item) => {
        map.set(item.id, item.name);
      });
      workerSkillOptions.forEach((item) => {
        if (!map.has(item.id)) {
          map.set(item.id, item.name);
        }
      });
      return Array.from(map.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (typeFilter === "FEATURED") {
      return baseSkillOptions;
    }
    return baseSkillOptions;
  }, [
    typeFilter,
    workerSkillOptions,
    baseSkillOptions,
  ]);

  const baseJurisdictionOptions = useMemo(() => {
    const map = new Map<number, string>();
    jurisdictionsRaw.forEach((item: any, index: number) => {
      const rawId = item.id ?? item.jurisdiccion_id ?? item.jurisdiccion?.id ?? index;
      const id = Number(rawId);
      const name = item.jurisdiccion_name ?? item.name ?? item.jurisdiccion?.name ?? "Jurisdicción";

      if (!map.has(Number.isNaN(id) ? index : id)) {
        map.set(Number.isNaN(id) ? index : id, String(name));
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({
        id,
        name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [jurisdictionsRaw]);

  const workerJurisdictions = useMemo(
    () =>
      buildJurisdictionStats(workerDataSource)
        .map((item) => ({
          id: item.id,
          name: item.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [buildJurisdictionStats, workerDataSource]
  );

  const companyJurisdictions = useMemo(
    () =>
      buildJurisdictionStats(
        filteredProfessionalProfiles.filter((profile) => profile.rawType === "COMPANY")
      )
        .map((item) => ({
          id: item.id,
          name: item.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [buildJurisdictionStats, filteredProfessionalProfiles]
  );

  const personJurisdictions = useMemo(
    () =>
      buildJurisdictionStats(
        filteredProfessionalProfiles.filter((profile) => profile.rawType !== "COMPANY")
      )
        .map((item) => ({
          id: item.id,
          name: item.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [buildJurisdictionStats, filteredProfessionalProfiles]
  );

  const combinedJurisdictions = useMemo(() => {
    const map = new Map<number, string>();
    baseJurisdictionOptions.forEach(({ id, name }) => map.set(id, name));
    buildJurisdictionStats(filteredProfessionalProfiles).forEach((item) => {
      if (!map.has(item.id)) {
        map.set(item.id, item.name);
      }
    });
    workerJurisdictions.forEach(({ id, name }) => {
      if (!map.has(id)) {
        map.set(id, name);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [baseJurisdictionOptions, buildJurisdictionStats, filteredProfessionalProfiles, workerJurisdictions]);

  const jurisdictionOptions = useMemo(() => {
    if (typeFilter === "WORKER") {
    const list = workerJurisdictions.length > 0 ? workerJurisdictions : baseJurisdictionOptions;
    return list;
    }
    if (typeFilter === "COMPANY") {
    const list = companyJurisdictions.length > 0 ? companyJurisdictions : baseJurisdictionOptions;
    return list;
    }
    if (typeFilter === "PROFESSIONAL") {
    const list = personJurisdictions.length > 0 ? personJurisdictions : baseJurisdictionOptions;
    return list;
    }
    if (typeFilter === "ALL") {
    return combinedJurisdictions;
    }
    if (typeFilter === "FEATURED") {
    return baseJurisdictionOptions;
    }
  return baseJurisdictionOptions;
  }, [typeFilter, baseJurisdictionOptions, workerJurisdictions, companyJurisdictions, personJurisdictions, combinedJurisdictions]);

  const professionalDisplayResults = useMemo(() => {
    if (showFeatured) {
      return featuredProfiles;
    }

    if (typeFilter === "PROFESSIONAL") {
      return filteredProfessionalProfiles.filter((profile) => profile.rawType !== "COMPANY");
    }

    if (typeFilter === "COMPANY") {
      return filteredProfessionalProfiles.filter((profile) => profile.rawType === "COMPANY");
    }

    return filteredProfessionalProfiles;
  }, [showFeatured, typeFilter, featuredProfiles, filteredProfessionalProfiles]);

  const combinedResults = useMemo(() => {
    if (showFeatured) {
      return featuredProfiles;
    }

    if (typeFilter === "WORKER") {
      return workerResults;
    }

    if (typeFilter === "ALL") {
      return [...filteredProfessionalProfiles, ...workerResults];
    }

    return professionalDisplayResults;
  }, [showFeatured, typeFilter, featuredProfiles, workerResults, filteredProfessionalProfiles, professionalDisplayResults]);

  const displayedProfiles = useMemo(
    () =>
      combinedResults.filter((profile) => {
        if (verifiedOnly && !profile.hasIdentityValidated) {
          return false;
        }
        return true;
      }),
    [combinedResults, verifiedOnly]
  );

  const professionalTotal = filteredMeta?.itemCount ?? filteredProfessionalProfiles.length;

  const totalCount = showFeatured
    ? featuredProfiles.length
    : typeFilter === "WORKER"
      ? workerResults.length
      : typeFilter === "ALL"
        ? professionalTotal + workerResults.length
        : professionalTotal;

  const pageForPagination = filteredMeta?.page || filterState.page;

  const usesRemotePagination = !showFeatured && typeFilter !== "WORKER";

  const handlePageChange = useCallback((nextPage: number) => {
    filterActions.setPage(nextPage);
    setHeroCollapsed(true);
  }, [filterActions]);

  const buildProfileNavigationQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set("from", "profesionales");
    if (filterState.skillIds[0]) {
      params.set("skill", String(filterState.skillIds[0]));
    }
    if (filterState.jurisdictionIds.length > 0) {
      params.set("jurisdictions", filterState.jurisdictionIds.join(","));
    }
    if (filterState.allCountry) {
      params.set("allCountry", "1");
    }
    if (pageForPagination > 1) {
      params.set("page", String(pageForPagination));
    }
    return params.toString();
  }, [
    filterState.allCountry,
    filterState.jurisdictionIds,
    filterState.skillIds,
    pageForPagination,
  ]);

  const handleViewProfile = (profile: PeopleSearchProfile) => {
    if (profile.type === "WORKER") return;
    premiumCheck(() => {
      const baseUrl = `/perfil/${profile.id}`;
      const query = buildProfileNavigationQuery();
      navigate(query ? `${baseUrl}?${query}` : baseUrl);
    })();
  };

  const handleContact = useCallback(
    (profile: PeopleSearchProfile) => {
      if (profile.type === "WORKER") {
    if (profile.contact.email) {
      window.open(`mailto:${profile.contact.email}`, "_blank");
      return;
    }
    if (profile.contact.phone) {
      window.open(`tel:${profile.contact.phone}`);
    }
        return;
      }
      const userData = profile.raw ?? profile;
      premiumCheck(() => {
        dispatch(openSendContactNotificationModal(userData));
      })();
    },
    [dispatch, premiumCheck]
  );

  const handleQuickSkill = (skillId: number) => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    filterActions.applyQuickSkill(skillId);
  };

  const handleSkillToggle = (id: number) => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    filterActions.toggleSkill(id);
  };

  const handleJurisdictionToggle = (id: number) => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    filterActions.toggleJurisdiction(id);
  };

  const handleToggleAllCountry = () => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    filterActions.toggleAllCountry();
  };

  const handleToggleVerified = () => {
    exitFeaturedMode();
    setHeroCollapsed(true);
    const next = !verifiedOnly;
    setVerifiedOnly(next);
    filterActions.applyFilters();
    const shouldFetchWorkers = typeFilter === "ALL" || typeFilter === "WORKER";
    if (shouldFetchWorkers) {
      if (hasFilterSelections || next) {
        dispatch(
          fetchWorkersByFilters({
            jurisdictions: filterState.allCountry ? undefined : filterState.jurisdictionIds,
            skills: filterState.skillIds,
            identityValidated: next ? true : undefined,
          })
        );
      } else {
        requestAllWorkers();
      }
    } else {
      clearWorkers();
    }
  };

  const handleClearFilters = () => {
    setHeroCollapsed(true);
    setVerifiedOnly(false);
    setTypeFilter("FEATURED");
    filterActions.clearFilters();
    clearWorkers();
  };

  useEffect(() => {
    if (typeFilter === "WORKER" || typeFilter === "ALL") {
      if (noFilterConstraints) {
        requestAllWorkers();
      }
      return;
    }

    clearWorkers();
  }, [typeFilter, noFilterConstraints, requestAllWorkers, clearWorkers]);

  return (
    <PeopleSearchLayout
      navigation={<NewNavImproved />}
      mobileNavigation={ocultarEnMobile ? <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} /> : null}
      breadcrumbs={<Breadcrumbs />}
      hero={<PeopleSearchHero quickSkills={quickSkills} onSelectSkill={handleQuickSkill} />}
      heroClassName={heroCollapsed ? layoutStyles.heroCollapsed : undefined}
      filters={
        <PeopleSearchFilters
          filterCount={filtersMeta.filtersCount}
          totalCount={totalCount}
          type={typeFilter}
          popularSkills={popularSkills}
          allSkills={skillOptions}
          jurisdictions={jurisdictionOptions}
          selectedSkillIds={filterState.skillIds}
          selectedJurisdictionIds={filterState.jurisdictionIds}
          allCountrySelected={filterState.allCountry}
          jurisdictionsLoaded={jurisdictionsLoaded}
          verifiedOnly={verifiedOnly}
          onTypeChange={(nextType) => {
            setHeroCollapsed(true);
            setTypeFilter(nextType);
            const shouldApply = hasFilterSelections || verifiedOnly || nextType !== "FEATURED";
            if (!shouldApply) {
              if (filterState.page !== 1) {
                filterActions.setPage(1);
              }
              return;
            }
            if (filterState.page !== 1) {
              filterActions.setPage(1);
              return;
            }
            filterActions.applyFilters();
          }}
          onSkillToggle={handleSkillToggle}
          onJurisdictionToggle={handleJurisdictionToggle}
          onToggleAllCountry={handleToggleAllCountry}
          onToggleVerified={handleToggleVerified}
          onClearFilters={handleClearFilters}
          onResetType={() => setTypeFilter("FEATURED")}
        />
      }
      results={
        <PeopleSearchResults
          isLoading={isLoading}
          type={typeFilter}
          profiles={displayedProfiles}
          totalCount={totalCount}
          currentPage={usesRemotePagination ? pageForPagination : undefined}
          pageSize={usesRemotePagination ? DEFAULT_PAGE_SIZE : undefined}
          onPageChange={usesRemotePagination ? handlePageChange : undefined}
          onViewProfile={handleViewProfile}
          onContactWorker={handleContact}
          onClearFilters={handleClearFilters}
          onOpenFilters={() => {
            setHeroCollapsed(false);
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }}
        />
      }
      mobileActions={null}
      shouldHideNavigation={ocultarEnMobile}
    />
  );
};

export default PeopleSearchPage;
